if (!customElements.get('product-custom-gallery')) {
  customElements.define('product-custom-gallery', class ProductCustomGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.currentNavigableIndex = 0;
      this.scrollPosition = 0;
      this.mediaData = [];
      
      // タッチ判定用
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
      
      // サムネイルスワイプ用
      this.thumbnailTouchStartX = 0;
      this.thumbnailTouchStartY = 0;
      this.thumbnailScrollIndex = 0;
      this.thumbnailStartScrollPosition = 0;
      this.thumbnailIsSwiping = false;
      
      // モバイル動画自動再生用
      this.userInteracted = false;
      
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      
      this.mql = window.matchMedia('(min-width: 750px)');
      
      // 初回タッチを検出
      document.addEventListener('touchstart', () => {
        this.userInteracted = true;
      }, { once: true, passive: true });
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (!this.elements.thumbnails) return;
      
      // データ抽出（シンプル化版）
      this.extractMediaData();
      
      // イベント設定
      this.setupEvents();
      
      // 初期表示
      if (this.mediaData.length > 0) {
        this.selectMedia(0);
      }
      
      // セマンティック削除
      if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    // シンプル化されたデータ抽出
    extractMediaData() {
      const mediaItems = this.querySelectorAll('.product__media-item');
      
      mediaItems.forEach((item, index) => {
        const img = item.querySelector('img');
        const video = item.querySelector('video');
        const infoArea = item.querySelector('.original-info-area');
        
        let mediaData;
        
        if (video) {
          const source = video.querySelector('source');
          mediaData = {
            type: 'video',
            element: video,
            src: source ? source.src : '',
            poster: video.poster || '',
            alt: video.title || `動画${index + 1}`
          };
        } else if (img) {
          mediaData = {
            type: 'image',
            element: img,
            src: img.src,
            srcset: img.srcset || '',
            sizes: img.sizes || '',
            alt: img.alt || `画像${index + 1}`
          };
        }
        
        // メタデータ取得
        if (mediaData && infoArea) {
          const metadata = this.extractMetadata(infoArea);
          Object.assign(mediaData, metadata);
        }
        
        if (mediaData) {
          this.mediaData.push(mediaData);
        }
      });
    }

    extractMetadata(infoArea) {
      const metadata = { model: '', size: '', color: '' };
      const paragraphs = infoArea.querySelectorAll('p');
      
      paragraphs.forEach(p => {
        const text = p.textContent.trim();
        if (text.includes('モデル:')) {
          metadata.model = text.replace('モデル:', '').trim();
        } else if (text.includes('サイズ:')) {
          metadata.size = text.replace('サイズ:', '').trim();
        } else if (text.includes('カラー:')) {
          metadata.color = text.replace('カラー:', '').trim();
        }
      });
      
      return metadata;
    }

    setupEvents() {
      // media-gallery.js からの機能
      this.elements.viewer.addEventListener('slideChanged', this.debounce(this.onSlideChanged.bind(this), 500));
      
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', 
          this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
        );
      });
      
      // new-blog-carousel.js からの機能
      this.setupMainImageEvents();
      this.setupThumbnailSwipeEvents();
      
      // キーボード
      document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.navigate('prev');
        if (e.key === 'ArrowRight') this.navigate('next');
      });
    }

    // media-gallery.js の機能群
    onSlideChanged(event) {
      if (!event.detail || !event.detail.currentElement) return;
      
      const currentElement = event.detail.currentElement;
      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${currentElement.dataset.mediaId}"]`
      );
      this.updateCurrentIndices(currentElement);
      this.setActiveThumbnail(thumbnail);
      
      // 🔧 サムネイルボタンを自動クリックして、deferred-mediaをアクティブ化
      if (thumbnail) {
        const thumbnailButton = thumbnail.querySelector('button');
        if (thumbnailButton && !thumbnailButton.hasAttribute('data-auto-clicked')) {
          thumbnailButton.setAttribute('data-auto-clicked', 'true');
          thumbnailButton.click();
          setTimeout(() => thumbnailButton.removeAttribute('data-auto-clicked'), 100);
        }
      }
      
      // スワイプ後も動画を自動再生
      this.playActiveMedia(currentElement);
    }

    setActiveMedia(mediaId, prepend) {
      let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      if (prepend && activeMedia.parentElement) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail && activeThumbnail.parentElement) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      activeMedia.classList.add('is-active');

      if (!prepend && !this.isNavigableMediaItem(activeMedia)) {
        const fallback = this.getNavigableMediaItems().find((item) => item !== activeMedia);
        if (fallback) {
          fallback.classList.add('is-active');
          activeMedia = fallback;
          mediaId = fallback.dataset.mediaId;
        }
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });

      this.playActiveMedia(activeMedia);
      this.updateCurrentIndices(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      if (!activeThumbnail) return;

      this.setActiveThumbnail(activeThumbnail);
      const position = activeThumbnail.dataset.mediaPosition;
      if (position) {
        this.announceLiveRegion(activeMedia, position);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => 
        element.removeAttribute('aria-current')
      );
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      
      // window.accessibilityStrings のチェックを追加
      if (!window.accessibilityStrings || !window.accessibilityStrings.imageAvailable) return;
      
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
      
      // 動画要素が読み込まれるまで待つ（最大10回試行）
      const findAndPlayVideo = (attemptCount = 0) => {
        const videoElement = activeItem.querySelector('video');
        
        if (videoElement) {
          this.configureVideoPlayback(videoElement);
        } else if (attemptCount < 10) {
          setTimeout(() => findAndPlayVideo(attemptCount + 1), 100);
        }
      };
      
      findAndPlayVideo();
    }

    configureVideoPlayback(videoElement) {
      if (!videoElement || videoElement.tagName !== 'VIDEO') return;
      
      // 基本設定
      videoElement.muted = true;
      videoElement.autoplay = true;
      videoElement.loop = true;
      videoElement.controls = false;
      videoElement.preload = 'metadata';
      
      // モバイル対応：インライン再生
      videoElement.setAttribute('playsinline', '');
      videoElement.playsInline = true;
      videoElement.setAttribute('autoplay', '');
      videoElement.setAttribute('loop', '');
      videoElement.setAttribute('muted', '');
      
      // コントロールを完全に削除
      videoElement.removeAttribute('controls');
      
      // CSS調整
      videoElement.style.width = '100%';
      videoElement.style.maxWidth = '100%';
      
      // 再生処理（リトライ付き）
      const attemptPlay = (retryCount = 0) => {
        const playPromise = videoElement.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // 再生成功
            })
            .catch((error) => {
              // モバイルで失敗した場合、最大5回リトライ
              if (retryCount < 5) {
                setTimeout(() => attemptPlay(retryCount + 1), 200);
              }
            });
        }
      };
      
      // 複数のタイミングで再生を試みる
      if (videoElement.readyState >= 2) {
        attemptPlay();
      } else {
        videoElement.addEventListener('loadedmetadata', () => attemptPlay(), { once: true });
        videoElement.addEventListener('canplay', () => attemptPlay(), { once: true });
      }
      
      // 即座にも試行
      setTimeout(() => attemptPlay(), 50);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    // new-blog-carousel.js の機能群
    setupMainImageEvents() {
      let touchHandled = false;
      
      const mediaItems = this.querySelectorAll('.product__media-item');
      const isMobile = () => window.innerWidth <= 768;
      
      mediaItems.forEach(item => {
        item.addEventListener('click', (e) => {
          const isExplicitModalOpener = e.target.closest('button[id^="ModalOpener"]');
          if (isExplicitModalOpener || this.isWithinIconArea(e.clientX, e.clientY, item)) {
            return;
          }
          
          if (touchHandled) {
            touchHandled = false;
            return;
          }
          
          // スマホではタップでの移動を無効化
          if (isMobile()) {
            return;
          }
          
          e.preventDefault();
          e.stopPropagation();
          this.handleClick(e, item);
        }, { capture: true });
    
        item.addEventListener('touchstart', (e) => {
          const touch = e.touches[0];
          this.touchStartX = touch.clientX;
          this.touchStartY = touch.clientY;
          this.touchStartTime = Date.now();
        }, { passive: true });
    
        item.addEventListener('touchend', (e) => {
          const isExplicitModalOpener = e.target.closest('button[id^="ModalOpener"]');
          const touch = e.changedTouches[0];
          if (isExplicitModalOpener || this.isWithinIconArea(touch.clientX, touch.clientY, item)) {
            return;
          }
          
          const deltaX = Math.abs(touch.clientX - this.touchStartX);
          const deltaY = Math.abs(touch.clientY - this.touchStartY);
          const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          const touchDuration = Date.now() - this.touchStartTime;
    
          // スマホではタップでの移動を無効化
          // スワイプのみ有効（スライダー側で処理される）
          if (totalDistance < 20 && touchDuration < 500) {
            touchHandled = true;
            e.preventDefault();
            // handleClick を呼ばない
          }
        }, { passive: false });
      });
    }
    
    isWithinIconArea(clientX, clientY, item) {
      const icon = item.querySelector('.product__media-icon');
      if (!icon) return false;
      
      const rect = icon.getBoundingClientRect();
      return clientX >= rect.left &&
             clientX <= rect.right &&
             clientY >= rect.top &&
             clientY <= rect.bottom;
    }
    
    handleClick(e, element) {
      const rect = element.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const centerX = rect.width / 2;
      
      if (clickX < centerX) {
        this.navigate('prev');
      } else {
        this.navigate('next');
      }
    }

    navigate(direction) {
      this.syncCurrentIndex();
      const navigableItems = this.getNavigableMediaItems();
      if (navigableItems.length <= 1) return;
      
      let newIndex;
      if (direction === 'prev') {
        newIndex = this.currentNavigableIndex === 0 ? navigableItems.length - 1 : this.currentNavigableIndex - 1;
      } else {
        newIndex = this.currentNavigableIndex === navigableItems.length - 1 ? 0 : this.currentNavigableIndex + 1;
      }
      
      this.selectMedia(newIndex);
    }

    selectMedia(index) {
      const navigableItems = this.getNavigableMediaItems();
      if (!navigableItems.length) return;
      
      const boundedIndex = ((index % navigableItems.length) + navigableItems.length) % navigableItems.length;
      const target = navigableItems[boundedIndex];
      if (!target) return;
      
      this.setActiveMedia(target.dataset.mediaId, false);
      this.currentNavigableIndex = boundedIndex;
    }

    setupThumbnailSwipeEvents() {
      // サムネイルスワイプを無効化
      // メイン画像のスワイプのみ有効にする
      return;
    }

    scrollThumbnails(direction) {
      const viewport = this.elements.thumbnails;
      const wrapper = viewport.querySelector('.thumbnail-list');
      const containerWidth = viewport.offsetWidth;
      const scrollAmount = containerWidth * 0.7;
      const maxScroll = wrapper.scrollWidth - containerWidth;
      
      if (direction === 'next') {
        if (this.scrollPosition >= maxScroll) {
          this.scrollPosition = 0;
        } else {
          this.scrollPosition = Math.min(maxScroll, this.scrollPosition + scrollAmount);
        }
      } else {
        if (this.scrollPosition <= 0) {
          this.scrollPosition = maxScroll;
        } else {
          this.scrollPosition = Math.max(0, this.scrollPosition - scrollAmount);
        }
      }
      
      wrapper.style.transform = `translateX(-${this.scrollPosition}px)`;
    }

    snapToNearestThumbnail() {
      const viewport = this.elements.thumbnails;
      const wrapper = viewport.querySelector('.thumbnail-list');
      const thumbnails = wrapper.querySelectorAll('.thumbnail-list__item');
      const containerWidth = viewport.offsetWidth;
      const containerCenter = containerWidth / 2;
      
      let closestIndex = 0;
      let minDistance = Infinity;
      
      thumbnails.forEach((thumbnail, index) => {
        const thumbnailLeft = thumbnail.offsetLeft;
        const thumbnailCenter = thumbnailLeft + thumbnail.offsetWidth / 2 - this.scrollPosition;
        const distance = Math.abs(thumbnailCenter - containerCenter);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });
      
      this.scrollToThumbnailIndex(closestIndex);
    }

    scrollToThumbnailIndex(index) {
      const viewport = this.elements.thumbnails;
      const wrapper = viewport.querySelector('.thumbnail-list');
      const thumbnails = wrapper.querySelectorAll('.thumbnail-list__item');
      
      if (!thumbnails[index]) return;
      
      const containerWidth = viewport.offsetWidth;
      const thumbnail = thumbnails[index];
      const thumbnailLeft = thumbnail.offsetLeft;
      const thumbnailWidth = thumbnail.offsetWidth;
      
      let targetPosition = thumbnailLeft - (containerWidth - thumbnailWidth) / 2;
      const maxScroll = wrapper.scrollWidth - containerWidth;
      targetPosition = Math.max(0, Math.min(maxScroll, targetPosition));
      
      this.scrollPosition = targetPosition;
      wrapper.style.transform = `translateX(-${this.scrollPosition}px)`;
    }

    // ユーティリティ
    getMediaItems() {
      return Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
    }

    getNavigableMediaItems() {
      return this.getMediaItems().filter((item) => this.isNavigableMediaItem(item));
    }

    isNavigableMediaItem(item) {
      if (!item) return false;
      if (!item.classList.contains('product__media-item--variant')) return true;
      const mediaItems = this.getMediaItems();
      const index = mediaItems.indexOf(item);
      return index === 0 || item.classList.contains('is-active');
    }

    updateCurrentIndices(activeMedia) {
      const mediaItems = this.getMediaItems();
      this.currentIndex = mediaItems.indexOf(activeMedia);

      const navigableItems = this.getNavigableMediaItems();
      const navigableIndex = navigableItems.indexOf(activeMedia);
      if (navigableIndex !== -1) {
        this.currentNavigableIndex = navigableIndex;
      } else if (navigableItems.length) {
        this.currentNavigableIndex = Math.min(this.currentNavigableIndex, navigableItems.length - 1);
      } else {
        this.currentNavigableIndex = -1;
      }
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    getMediaIndex(element) {
      if (!element) return -1;
      return this.getMediaItems().indexOf(element);
    }

    syncCurrentIndex() {
      const activeItem = this.elements.viewer.querySelector('.product__media-item.is-active');
      if (!activeItem) return;

      this.updateCurrentIndices(activeItem);

      if (!this.isNavigableMediaItem(activeItem)) {
        const navigableItems = this.getNavigableMediaItems();
        if (!navigableItems.length) return;
        const fallback = navigableItems[0];
        if (fallback && fallback !== activeItem) {
          this.setActiveMedia(fallback.dataset.mediaId, false);
        }
      }
    }
  });
}
